import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtDate } from "./shared";
import { toast } from "sonner";
import { FileSignature, FileCheck2, Send, Trash2, Download, Plus, X, BellRing } from "lucide-react";

const STATUS_BADGE = {
    draft: { label: "Bozza", cls: "bg-ink-900 text-ink-400 border-white/15" },
    sent: { label: "In attesa di firma", cls: "bg-blue-900/25 text-blue-300 border-blue-600/40" },
    signed: { label: "FIRMATO", cls: "bg-green-900/30 text-green-300 border-green-700/40" },
};

export default function CrmContracts() {
    const [data, setData] = useState(null);
    const [filter, setFilter] = useState("");
    const [showNew, setShowNew] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await api.get("/crm/contracts", { params: filter ? { status: filter } : {} });
            setData(r.data);
        } catch { setData({ items: [], counts: {} }); }
    }, [filter]);
    useEffect(() => { load(); }, [load]);

    const send = async (id) => {
        if (!window.confirm("Inviare al cliente il link personale per leggere e firmare graficamente il documento?")) return;
        try {
            const r = await api.post(`/crm/contracts/${id}/send`);
            r.data.email_sent ? toast.success("Link di firma inviato al cliente.") : toast.warning("Documento pronto, ma l'email non è stata inviata.");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore invio"); }
    };

    const remind = async (id) => {
        try {
            await api.post(`/crm/contracts/${id}/remind`);
            toast.success("Nuovo link personale di firma inviato.");
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore invio promemoria"); }
    };

    const remove = async (id) => {
        if (!window.confirm("Eliminare questo documento?")) return;
        try { await api.delete(`/crm/contracts/${id}`); load(); }
        catch (e) { window.alert(e?.response?.data?.detail || "Errore"); }
    };

    if (!data) return <div className="text-ink-400 text-sm py-12 text-center">Caricamento contratti...</div>;
    const c = data.counts || {};

    return (
        <div data-testid="crm-contracts" className="space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[["total", "Totale", c.total], ["draft", "Bozze", c.draft], ["sent", "In firma", c.sent], ["signed", "Firmati", c.signed]].map(([k, label, val]) => (
                    <div key={k} className="border border-white/10 bg-ink-900/40 p-4">
                        <div className="text-[10px] uppercase tracking-wider text-ink-400">{label}</div>
                        <div className={`font-serif text-2xl mt-1 ${k === "signed" ? "text-green-300" : "text-ink-50"}`}>{val || 0}</div>
                    </div>
                ))}
            </div>

            <div className="flex flex-wrap gap-2 items-center">
                <select data-testid="contracts-filter" value={filter} onChange={(e) => setFilter(e.target.value)}
                    className="bg-ink-950 border border-white/15 px-3 py-2.5 text-sm text-ink-50 outline-none">
                    <option value="">Tutti gli stati</option>
                    <option value="draft">Bozze</option>
                    <option value="sent">In attesa di firma</option>
                    <option value="signed">Firmati</option>
                </select>
                <div className="flex-1" />
                <button data-testid="new-contract-btn" onClick={() => setShowNew(true)} className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2.5 text-xs flex items-center gap-2 transition-colors rounded-full">
                    <Plus size={14} /> Nuovo incarico / documento
                </button>
            </div>

            <div className="border border-white/10">
                {(data.items || []).length === 0 && <div className="p-8 text-center text-xs text-ink-500">Nessun documento. Crea il primo incarico dal bottone qui sopra.</div>}
                {(data.items || []).map((ct) => {
                    const b = STATUS_BADGE[ct.status] || STATUS_BADGE.draft;
                    return (
                        <div key={ct.id} data-testid={`contract-row-${ct.id}`} className="flex items-center gap-3 px-4 py-3 border-b border-white/5 flex-wrap">
                            {ct.status === "signed" ? <FileCheck2 size={15} className="text-green-300 shrink-0" /> : <FileSignature size={15} className="text-blue-400 shrink-0" />}
                            <div className="flex-1 min-w-[200px]">
                                <div className="text-sm text-ink-50 truncate">{ct.title}</div>
                                <div className="text-[11px] text-ink-400">{ct.lead_name} · {fmtDate(ct.created_at)}{ct.status === "signed" && ct.signature?.signed_at ? ` · firmato ${fmtDate(ct.signature.signed_at)}` : ""}</div>
                            </div>
                            <span data-testid={`contract-status-${ct.id}`} className={`text-[9px] uppercase tracking-wider px-2 py-1 border ${b.cls}`}>{b.label}</span>
                            <div className="flex gap-1.5">
                                <button data-testid={`contract-download-${ct.id}`} title="Scarica PDF"
                                    onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/crm/contracts/${ct.id}/pdf`, "_blank")}
                                    className="p-2 text-ink-400 hover:text-blue-400 border border-white/10"><Download size={13} /></button>
                                {ct.status === "draft" && (
                                    <button data-testid={`contract-send-${ct.id}`} title="Invia per firma" onClick={() => send(ct.id)}
                                        className="p-2 text-ink-400 hover:text-blue-400 border border-white/10"><Send size={13} /></button>
                                )}
                                {ct.status === "sent" && (
                                    <button data-testid={`contract-remind-${ct.id}`} title="Genera e rinvia un nuovo link di firma" onClick={() => remind(ct.id)}
                                        className="p-2 text-ink-400 hover:text-blue-400 border border-white/10"><BellRing size={13} /></button>
                                )}
                                {ct.status !== "signed" && (
                                    <button data-testid={`contract-delete-${ct.id}`} title="Elimina" onClick={() => remove(ct.id)}
                                        className="p-2 text-ink-400 hover:text-red-300 border border-white/10"><Trash2 size={13} /></button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {showNew && <NewContractModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
        </div>
    );
}

function NewContractModal({ onClose, onCreated }) {
    const [leads, setLeads] = useState([]);
    const [form, setForm] = useState({ lead_id: "", contract_type: "incarico_vendita", title: "", content: "" });
    const [busy, setBusy] = useState(false);
    const [loadingTpl, setLoadingTpl] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.get("/crm/leads").then((r) => setLeads(r.data.items || [])).catch(() => {});
    }, []);

    const loadTemplate = async (leadId, ctype) => {
        if (!leadId) return;
        setLoadingTpl(true);
        try {
            const r = await api.get("/crm/contracts/template", { params: { lead_id: leadId, contract_type: ctype } });
            setForm((f) => ({ ...f, lead_id: leadId, contract_type: ctype, title: r.data.title, content: r.data.content }));
        } catch (e) { console.error(e); }
        finally { setLoadingTpl(false); }
    };

    const create = async () => {
        setBusy(true); setError(null);
        try {
            await api.post("/crm/contracts", form);
            onCreated();
        } catch (e) {
            setError(e?.response?.data?.detail || "Errore creazione");
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-[60]">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
            <div data-testid="new-contract-modal" className="absolute inset-x-0 bottom-0 sm:inset-0 sm:m-auto sm:h-fit sm:max-w-2xl bg-ink-950 border border-white/15 max-h-[92vh] overflow-y-auto p-6">
                <div className="flex items-center justify-between mb-5">
                    <h3 className="font-serif text-xl text-ink-50">Nuovo incarico / documento</h3>
                    <button onClick={onClose} className="text-ink-400 hover:text-ink-50"><X size={18} /></button>
                </div>
                <div className="grid sm:grid-cols-2 gap-3 mb-3">
                    <div>
                        <label className="block text-[10px] text-ink-400 uppercase tracking-wider mb-1">Cliente / Lead</label>
                        <select data-testid="contract-lead" value={form.lead_id} onChange={(e) => loadTemplate(e.target.value, form.contract_type)}
                            className="w-full bg-ink-950 border border-white/15 px-3 py-2.5 text-sm text-ink-50 outline-none">
                            <option value="">— Seleziona —</option>
                            {leads.map((l) => <option key={l.id} value={l.id}>{l.name}{l.kind === "seller" ? " (venditore)" : ""}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-[10px] text-ink-400 uppercase tracking-wider mb-1">Tipo documento</label>
                        <select data-testid="contract-type" value={form.contract_type} onChange={(e) => loadTemplate(form.lead_id, e.target.value)}
                            className="w-full bg-ink-950 border border-white/15 px-3 py-2.5 text-sm text-ink-50 outline-none">
                            <option value="incarico_vendita">Incarico di vendita</option>
                            <option value="incarico_locazione">Incarico di locazione</option>
                            <option value="proposta_acquisto">Proposta di acquisto</option>
                            <option value="altro">Altro documento</option>
                        </select>
                    </div>
                </div>
                <label className="block text-[10px] text-ink-400 uppercase tracking-wider mb-1">Titolo</label>
                <input data-testid="contract-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="w-full bg-transparent border border-white/15 px-3 py-2.5 text-sm text-ink-50 focus:border-blue-500 outline-none mb-3" />
                <label className="block text-[10px] text-ink-400 uppercase tracking-wider mb-1">Testo del documento {loadingTpl && <span className="text-blue-400">(caricamento template...)</span>}</label>
                <textarea data-testid="contract-content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={12}
                    placeholder="Seleziona un cliente per precompilare il template..."
                    className="w-full bg-transparent border border-white/15 px-3 py-2.5 text-xs text-ink-50 focus:border-blue-500 outline-none font-mono leading-relaxed" />
                {error && <p className="text-red-300 text-xs mt-2">{error}</p>}
                <button data-testid="contract-create" onClick={create} disabled={busy || !form.lead_id || !form.title.trim() || !form.content.trim()}
                    className="w-full py-3 text-sm mt-4 disabled:opacity-40 bg-blue-600 hover:bg-blue-500 text-white rounded-full transition-colors">{busy ? "Creazione..." : "Crea bozza"}</button>
                <p className="text-[10px] text-ink-500 mt-2 text-center">La bozza potrà essere inviata al cliente tramite un link personale per la firma grafica elettronica.</p>
            </div>
        </div>
    );
}
